import { useCallback, useMemo } from 'react';
import {
  buildEditorsHelperText,
  isPersonInSelection,
  mapEditorToAutocompleteOption,
} from '../../../../utils/case-plan-create.util';
import { useEditorSearchHook } from './useEditorSearchHook';

/**
 * State, search, and selection logic for the Editors section.
 * Keeps EditorsSection purely presentational.
 * @param {object[]} editors
 * @param {(editors: object[]) => void} onEditorsChange
 */
export const useEditorsSectionHook = (editors, onEditorsChange) => {
  const { options, isLoading, handleSearchChange } = useEditorSearchHook();

  const selectedMembers = useMemo(
    () => editors.map(mapEditorToAutocompleteOption).filter(Boolean),
    [editors],
  );

  const memberOptions = useMemo(
    () => options.map(mapEditorToAutocompleteOption).filter(Boolean),
    [options],
  );

  const handleMembersChange = useCallback(
    (nextValue) => {
      const nextEditors = Array.isArray(nextValue) ? nextValue : [];
      // Preserve the creator flag when options are remapped after a search.
      const withCreatorFlags = nextEditors.map((editor) => {
        const existing = editors.find(
          (item) =>
            item.personId === editor.personId ||
            item.username === editor.username,
        );
        return {
          ...editor,
          isCreator: Boolean(existing?.isCreator || editor.isCreator),
        };
      });
      onEditorsChange(withCreatorFlags);
    },
    [editors, onEditorsChange],
  );

  const handleRemoveEditor = useCallback(
    (editorToRemove) => {
      if (editorToRemove.isCreator) {
        return;
      }
      onEditorsChange(
        editors.filter((editor) => editor.personId !== editorToRemove.personId),
      );
    },
    [editors, onEditorsChange],
  );

  const getOptionDisabled = useCallback(
    (option) => isPersonInSelection(option, editors),
    [editors],
  );

  const helperText = buildEditorsHelperText(editors.length);

  return {
    isLoading,
    selectedMembers,
    memberOptions,
    helperText,
    handleSearchChange,
    handleMembersChange,
    handleRemoveEditor,
    getOptionDisabled,
  };
};
